import { queryClient } from '@/lib/queryClient';
import { ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';
import { translateServerText } from '@/lib/server-text';

/**
 * ZIP in both directions: upload a ZIP and have it unpacked into folders, and
 * download a whole folder as one ZIP. The unpack runs on the server as a job;
 * ZipImportPanel (in the shell) shows its progress. Started from anywhere —
 * upload button, drag-and-drop, the rail's folder menu — via a window event.
 */

export interface ZipJob {
  id: string;
  zipName: string;
  status: 'checking' | 'unpacking' | 'confirm' | 'importing' | 'done' | 'failed';
  error?: string;
  total: number;
  done: number;
  created: number;
  foldersCreated: number;
  conflicts: number;
  conflictNames: string[];
  replaced: number;
  skipped: { name: string; reason: string }[];
  failed: { name: string; reason: string }[];
}

export const ZIP_JOB_EVENT = 'dam:zip-job';
const CSRF = { 'X-Requested-With': 'ecomstat-dam' };

export const isZip = (f: File) =>
  /\.zip$/i.test(f.name) || f.type === 'application/zip' || f.type === 'application/x-zip-compressed';

async function errorOf(res: Response): Promise<ApiError> {
  const e = (await res.json().catch(() => ({}))) as { error?: string };
  return new ApiError(res.status, e.error ?? res.statusText);
}

export async function startZipUpload(folderId: number | null, file: File): Promise<ZipJob> {
  const form = new FormData();
  form.append('file', file);
  // Ask before overwriting files that already exist (ZipImportPanel shows the prompt).
  form.append('mode', 'ask');
  if (folderId) form.append('folderId', String(folderId));
  const res = await fetch('/api/upload-zip', { method: 'POST', credentials: 'same-origin', headers: CSRF, body: form });
  if (!res.ok) throw await errorOf(res);
  const { job } = (await res.json()) as { job: ZipJob };
  window.dispatchEvent(new CustomEvent(ZIP_JOB_EVENT, { detail: job }));
  return job;
}

export async function zipJob(id: string): Promise<ZipJob> {
  const res = await fetch(`/api/upload-zip/${encodeURIComponent(id)}`, { credentials: 'same-origin' });
  if (!res.ok) throw await errorOf(res);
  return ((await res.json()) as { job: ZipJob }).job;
}

/** Answer "N files already exist — overwrite?" for a paused import. */
export async function decideZipJob(id: string, overwrite: boolean): Promise<void> {
  const res = await fetch(`/api/upload-zip/${encodeURIComponent(id)}/decision`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { ...CSRF, 'Content-Type': 'application/json' },
    body: JSON.stringify({ overwrite }),
  });
  if (!res.ok && res.status !== 409) throw await errorOf(res);
}

/**
 * Upload a batch: plain files go through `uploadOne` as before; each ZIP asks
 * once whether to unpack it into folders (the old DAM's behaviour) or keep it
 * as a single .zip file.
 */
export async function uploadFiles(
  files: File[],
  folderId: number | null,
  uploadOne: (f: File) => Promise<unknown>,
  onError: (message: string) => void,
): Promise<void> {
  for (const f of files) {
    if (isZip(f) && window.confirm(t('zip.unpackConfirm', { name: f.name }))) {
      try {
        await startZipUpload(folderId, f);
      } catch (e) {
        onError(e instanceof ApiError ? e.message : translateServerText(String(e)));
      }
      continue;
    }
    await uploadOne(f).catch(() => {});
  }
}

/** Refresh what a finished import changed. */
export function refreshAfterImport(): void {
  void queryClient.invalidateQueries({ queryKey: ['folders'] });
  void queryClient.invalidateQueries({ queryKey: ['assets'] });
  void queryClient.invalidateQueries({ queryKey: ['library-stats'] });
}

/** Download a folder as ZIP after checking it fits; explains a refusal instead of failing. */
export async function downloadFolderZip(id: number, name: string, onError: (message: string) => void): Promise<void> {
  const res = await fetch(`/api/folders/${id}/zip?check=1`, { credentials: 'same-origin' });
  if (!res.ok) return onError((await errorOf(res)).message);
  const c = (await res.json()) as { ok: boolean; count: number; bytes: number; maxFiles: number; maxBytes: number };
  if (c.count === 0) return onError(t('zip.folderEmpty', { name }));
  if (!c.ok) {
    return onError(
      t('zip.folderTooLarge', {
        name,
        count: c.count.toLocaleString(),
        size: `${(c.bytes / 1024 ** 3).toFixed(1)} GB`,
        maxFiles: c.maxFiles.toLocaleString(),
        maxSize: `${(c.maxBytes / 1024 ** 3).toFixed(1)} GB`,
      }),
    );
  }
  window.location.href = `/api/folders/${id}/zip`;
}
